import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { fetchPlanningByArea, fetchAreaList, fetchActivityDetails } from './api';

const AreaLog = () => {
  const [activities, setActivities] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [planningAreas, setPlanningAreas] = useState([]); // Areas from planning API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMeta, setDisplayMeta] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Refs for scroll sync
  const headerScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const fixedScrollRef = useRef(null);
  
  // Sync horizontal scroll between header, content, and fixed scrollbar
  const handleContentScroll = (e) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (fixedScrollRef.current) {
      fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  const handleHeaderScroll = (e) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (fixedScrollRef.current) {
      fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  const handleFixedScroll = (e) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Fetch area list on mount
  useEffect(() => {
    const loadAreaList = async () => {
      try {
        const response = await fetchAreaList();
        setAreaList(response.data || []);
      } catch (err) {
        console.error('Error loading area list:', err);
      }
    };
    loadAreaList();
  }, []);

  // Fetch planning data by area
  const loadPlanningData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use today's date for the API call
      const today = new Date();
      const response = await fetchPlanningByArea(today);
      setActivities(response.activities || []);
      setDisplayMeta(response.meta || null);
      
      // Extract areas from planning API response if available
      // The planning API may return area list with sequence/order
      // Check multiple possible response structures
      let areaData = null;
      
      if (response.areas && Array.isArray(response.areas)) {
        areaData = response.areas;
      } else if (response.data && response.data.areas && Array.isArray(response.data.areas)) {
        areaData = response.data.areas;
      } else if (response.data && Array.isArray(response.data)) {
        // If data is an array, it might be area list
        areaData = response.data;
      }
      
      if (areaData) {
        setPlanningAreas(areaData);
      } else {
        // If no areas in planning response, extract unique areas from activities
        // and try to get sequence from activities if available
        const uniqueAreas = new Map();
        (response.activities || []).forEach(activity => {
          const areaName = activity.area_name || activity.area_id;
          if (areaName && !uniqueAreas.has(areaName)) {
            uniqueAreas.set(areaName, {
              area_name: areaName,
              area_id: activity.area_id,
              sequence: activity.area_sequence !== undefined ? activity.area_sequence : null
            });
          }
        });
        if (uniqueAreas.size > 0) {
          setPlanningAreas(Array.from(uniqueAreas.values()));
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching activities by area:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlanningData();
    
    // Optional: Set up polling for real-time updates
    const interval = setInterval(loadPlanningData, 60000);
    
    return () => clearInterval(interval);
  }, [loadPlanningData]);

  // Calculate the display range: round current time UP to next hour, then go back 23 more hours
  const displayEndTime = useMemo(() => {
    // Always use current time, ignore API metadata for dynamic display
    const rounded = new Date(currentTime);
    
    // Round UP to next hour (ceiling)
    // If it's 9:15, show 10:00. If it's 9:01, show 10:00. If it's 9:00 exactly, show 9:00.
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    
    return rounded;
  }, [currentTime]);

  const displayStartTime = useMemo(() => {
    // Go back 23 hours from displayEndTime
    return new Date(displayEndTime.getTime() - 23 * 60 * 60 * 1000);
  }, [displayEndTime]);

  // Calculate current time marker position
  // Layout: Left edge = displayEndTime (10:00), time flows right-to-left
  // So position = minutes BEFORE displayEndTime
  // If current time is 9:52 and displayEndTime is 10:00, marker is 8 minutes from left
  const currentTimeMarkerPosition = useMemo(() => {
    const minutesBeforeEnd = (displayEndTime.getTime() - currentTime.getTime()) / (60 * 1000);
    const position = (minutesBeforeEnd / 60) * 64;
    
    return Math.max(0, position);
  }, [currentTime, displayEndTime]);

  // Activity type colors
  const activityColors = {
    manufacturing: '#3b82f6',
    cleaning: '#10b981',
    maintenance: '#f59e0b',
  };

  // Use area-list API as primary source (shows all areas)
  // Merge sequence from planning API if available
  // Sort by sequence from API
  const allAreas = useMemo(() => {
    // Create a map of planning areas by name for quick lookup of sequence
    const planningAreasMap = new Map();
    planningAreas.forEach(area => {
      const name = area.area_name || area.name || area.area_id || area.id;
      if (name) {
        planningAreasMap.set(name, area);
      }
    });
    
    // Use areaList as the primary source (all areas from API)
    return areaList
      .map(area => {
        // Get area name from area-list API
        const areaName = area.area_name || area.name || area.area_id || area.id;
        
        // Try to get sequence from planning areas if available
        const planningArea = planningAreasMap.get(areaName);
        const sequence = planningArea?.sequence !== undefined && planningArea?.sequence !== null
          ? Number(planningArea.sequence)
          : (area.sequence !== undefined && area.sequence !== null 
              ? Number(area.sequence) 
              : null);
        
        return { 
          id: areaName, 
          name: areaName,
          sequence: sequence
        };
      })
      .sort((a, b) => {
        // Sort by sequence if available (API-defined order)
        if (a.sequence !== null && b.sequence !== null) {
          return a.sequence - b.sequence;
        }
        // If only one has sequence, prioritize it
        if (a.sequence !== null) return -1;
        if (b.sequence !== null) return 1;
        // If neither has sequence, sort alphabetically with numeric support
        const nameA = a?.name != null ? String(a.name) : '';
        const nameB = b?.name != null ? String(b.name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [areaList, planningAreas]);

  // Generate 24 hours array in reverse (current time = 0, going back to -23)
  // Depends on displayEndTime to recalculate when time changes
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => -i), [displayEndTime]);

  // Calculate activity position and width for each hour block
  // Match activities by area_name instead of area_id
  const getActivityBlocks = useCallback((areaName) => {
    const startTime = displayStartTime;
    const endTime = displayEndTime;

    return activities
      .filter(activity => {
        const activityAreaName = activity.area_name || activity.area_id;
        if (activityAreaName !== areaName) return false;
        const activityStart = new Date(activity.start_time);
        // If no end_time, use current time (ongoing activity)
        const activityEnd = activity.end_time ? new Date(activity.end_time) : currentTime;
        return activityStart < endTime && activityEnd > startTime;
      })
      .map(activity => {
        const activityStart = new Date(activity.start_time);
        // If no end_time, use current time (ongoing activity)
        const isOngoing = !activity.end_time;
        const activityEnd = isOngoing ? currentTime : new Date(activity.end_time);
        
        const minutesFromEnd = (endTime - activityEnd) / (60 * 1000);
        const activityDuration = (activityEnd - activityStart) / (60 * 1000);
        
        const startPosition = (minutesFromEnd / 60) * 64;
        const width = (activityDuration / 60) * 64;
        
        return {
          ...activity,
          startPosition: Math.max(0, startPosition),
          width: Math.min(24 * 64, width),
          isOngoing, // Flag for striped styling
        };
      });
  }, [activities, displayStartTime, displayEndTime, currentTime]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  if (loading && activities.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading area schedule...</div>
        </div>
        <style>
          {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (error && activities.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#991b1b', marginBottom: '12px' }}>
            Error Loading Data
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            {error}
          </div>
          <button
            onClick={loadPlanningData}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Area Log
          </h1>
          <a 
            href="https://staging.nucleus.plus" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: '500',
              textDecoration: 'none'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            {/* Go to Nucleus */}
            Staging Nucleus →
          </a>
        </div>
        
        {/* Time Range Display */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#991b1b'
          }}>
            ← NOW: {formatTime(currentTime)}
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing last 24 hours (1-hour blocks, reverse chronological)
          </div>
        </div>
        
        <div style={{ color: '#6b7280', fontSize: '13px' }}>
          From: {formatDate(displayStartTime)} {formatTime(displayStartTime)} → To: {formatDate(displayEndTime)} {formatTime(displayEndTime)}
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.manufacturing,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Manufacturing</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.cleaning,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Cleaning</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.maintenance,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Maintenance</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {/* Sticky Header Row */}
        <div 
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          className="header-scroll-hidden"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e5e7eb',
            minWidth: '1200px'
          }}>
            <div style={{ 
              width: '150px', 
              padding: '12px',
              fontWeight: '600',
              backgroundColor: '#f9fafb',
              position: 'sticky',
              left: 0,
              zIndex: 110,
              borderRight: '2px solid #e5e7eb',
              flexShrink: 0
            }}>
              Area
            </div>
            {hours.map(hourOffset => {
              const hourTime = new Date(displayEndTime.getTime() + hourOffset * 60 * 60 * 1000);
              return (
                <div
                  key={hourOffset}
                  style={{
                    width: '64px',
                    minWidth: '64px',
                    maxWidth: '64px',
                    boxSizing: 'border-box',
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: hourOffset === 0 ? '#fee2e2' : '#f9fafb',
                    borderRight: '1px solid #e5e7eb',
                    borderLeft: hourOffset === 0 ? '2px solid #ef4444' : '1px solid transparent'
                  }}
                >
                  <div style={{ fontWeight: '600', color: hourOffset === 0 ? '#991b1b' : '#374151' }}>
                    {hourOffset === 0 ? 'NOW' : `${hourOffset}h`}
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                    {hourTime.getHours().toString().padStart(2, '0')}:00
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scrollable Equipment Rows */}
        <div 
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          className="content-scroll-hidden"
          style={{ overflowX: 'auto' }}
        >
          <div style={{ minWidth: '1200px', position: 'relative' }}>
          {/* Area Rows */}
          {allAreas.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              No area data available
            </div>
          ) : (
            allAreas.map((area) => {
              const activityBlocks = getActivityBlocks(area.id);
              
              return (
                <div
                  key={area.id}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',
                    minHeight: '50px'
                  }}
                >
                  {/* Area Name */}
                  <div style={{
                    width: '150px',
                    padding: '8px 12px',
                    fontWeight: '500',
                    fontSize: '13px',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    borderRight: '2px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    {area.name}
                  </div>

                  {/* Hour Blocks */}
                  <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
                    {hours.map(hourOffset => (
                      <div
                        key={hourOffset}
                        style={{
                          width: '64px',
                          minWidth: '64px',
                          maxWidth: '64px',
                          boxSizing: 'border-box',
                          borderRight: '1px solid #e5e7eb',
                          borderLeft: hourOffset === 0 ? '2px solid #ef4444' : '1px solid transparent',
                          backgroundColor: hourOffset === 0 ? '#fef2f2' : (Math.abs(hourOffset) % 2 === 0 ? '#ffffff' : '#fafafa')
                        }}
                      />
                    ))}

                    {/* Activity Blocks */}
                    {activityBlocks.map(activity => {
                      const baseColor = activityColors[activity.activity_type] || '#6b7280';
                      
                      // Create striped pattern for ongoing activities (no end time) - use same activity color
                      const backgroundStyle = activity.isOngoing
                        ? {
                            background: `repeating-linear-gradient(45deg, ${baseColor}, ${baseColor} 4px, ${baseColor}dd 4px, ${baseColor}dd 8px)`,
                          }
                        : {
                            backgroundColor: baseColor,
                          };
                      
                      return (
                        <div
                          key={activity.id}
                          style={{
                            position: 'absolute',
                            left: `${activity.startPosition}px`,
                            width: `${activity.width}px`,
                            top: '6px',
                            bottom: '6px',
                            ...backgroundStyle,
                            borderRadius: '3px',
                            padding: '4px 6px',
                            color: 'white',
                            fontSize: '11px',
                            overflow: 'hidden',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            border: activity.isOngoing ? '1px dashed rgba(255, 255, 255, 0.5)' : 'none',
                          }}
                          title={`${activity.activity_type}${activity.isOngoing ? ' (ongoing)' : ''} - ${activity.batch_number || activity.cleaning_type || 'maintenance'}`}
                        >
                          <div style={{ fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {activity.batch_number || activity.cleaning_type || ''}
                            {activity.isOngoing && ' ⏳'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}

          {/* Current Time Marker */}
          <div
            style={{
              position: 'absolute',
              left: `${150 + currentTimeMarkerPosition}px`,
              top: '0',
              bottom: '0',
              width: '2px',
              backgroundColor: '#ef4444',
              zIndex: 20,
              pointerEvents: 'none'
            }}
          >
            {/* Circle marker at top */}
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                left: '-6px',
                width: '14px',
                height: '14px',
                backgroundColor: '#ef4444',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
            {/* Time tag below the circle */}
            <div
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                zIndex: 25
              }}
            >
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Fixed Horizontal Scrollbar at Bottom */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '24px',
          backgroundColor: '#e5e7eb',
          borderTop: '2px solid #cbd5e1',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px'
        }}
      >
        <div
          ref={fixedScrollRef}
          onScroll={handleFixedScroll}
          className="visible-scrollbar"
          style={{
            flex: 1,
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}
        >
          <div style={{ width: '2000px', height: '1px' }} />
        </div>
      </div>

      {/* Make scrollbar always visible */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
          
          .visible-scrollbar {
            scrollbar-width: auto !important;
            scrollbar-color: #64748b #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar {
            height: 16px !important;
            display: block !important;
          }
          .visible-scrollbar::-webkit-scrollbar-track {
            background: #d1d5db !important;
            border-radius: 8px !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb {
            background: #64748b !important;
            border-radius: 8px !important;
            border: 3px solid #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #475569 !important;
          }
        `}
      </style>

      {/* Scrollbar styles */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      
      {/* Add padding at bottom for fixed scrollbar */}
      <div style={{ height: '25px' }} />
    </div>
  );
};

export default AreaLog;
